import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import type { PlaneSize } from '@Components/BigBrowse/Model/ModelTypes';
import { getTextBlockSize } from '@Utils/3d';
import { Assets } from '@Utils/AssetsStore';
import { getParagraphs } from '@Utils/miscUtils';
import { scale } from '@Utils/window';
import { Dimensions } from 'react-native';

import type {
  CtaView,
  HeroAndTextView,
  HeroAndTextViewStyles,
  ParagraphView,
} from '../../common/MultiSectionView.types';

const Constants = {
  FIGMA_PAGE_WIDTH: 1440,
  FIGMA_PAGE_HEIGHT: 622,
  FIGMA_PAGE_TOP_PADDING: 183,
  FIGMA_PAGE_BOTTOM_PADDING: 151,
  FIGMA_PAGE_HORIZONTAL_PADDING: 150,
  FIGMA_COLUMNS_GAP: 76,
  CTA_TOP_MARGIN: 40,
};

function getCtaPosition(paragraphs: ParagraphView[]): CtaView['position'] {
  return paragraphs.length === 0
    ? 'moreCta_under_title'
    : 'moreCta_under_paragraphs';
}

export const getTextPart = async (
  section: HeroAndTextView
): Promise<HeroAndTextViewStyles['textPart']> => {
  const pageWidth = Dimensions.get('window').width;

  const titleColumnWidth = getTitleColumnWidth();
  const titleColumnPositionX = getTitleColumnPositionX(titleColumnWidth);

  const paragraphsColumnWidth = getParagraphColumnWidth();
  const paragraphsColumnPositionX = getParagraphsColumnPositionX(
    paragraphsColumnWidth
  );

  const paragraphsTexts = getParagraphsText(section);
  const paragraphs = await getParagraphsViews(
    paragraphsTexts,
    paragraphsColumnWidth,
    section.dtoData.fontColour
  );
  const ctaPosition = getCtaPosition(paragraphs);

  const title = await getTitle({
    section,
    columnWidth: titleColumnWidth,
  });

  const cta = getCta(section, ctaPosition);
  const columnsHeight = getColumnsHeight(title.size.height, paragraphs, cta);

  const pageHeight = getPageHeight(columnsHeight);
  const columnsPositionY = getColumnsPositionY(columnsHeight, pageHeight);

  return {
    page: getPage(pageWidth, pageHeight),
    titleColumn: {
      size: getTitleColumnSize(titleColumnWidth, columnsHeight),
      position: { x: titleColumnPositionX, y: columnsPositionY },
    },
    title,
    paragraphsColumn: {
      size: { width: paragraphsColumnWidth, height: columnsHeight },
      position: { x: paragraphsColumnPositionX, y: columnsPositionY },
    },
    paragraphs,
    cta,
  };
};

const scaleOnFigmaPageWidth = (width: number) =>
  scale(width, Dimensions.get('window').width, Constants.FIGMA_PAGE_WIDTH);

function getParagraphsHeight(paragraphs: ParagraphView[], cta?: CtaView) {
  const paragraphsHeights = paragraphs.reduce(
    (acc, paragraphView) => acc + paragraphView.size.height,
    0
  );

  const paragraphsPaddings =
    paragraphs.length <= 1
      ? 0
      : (paragraphs.length - 1) *
        paragraphs[0].props.lineHeight! *
        paragraphs[0].props.fontSize!;

  const ctaHeight =
    cta?.position === 'moreCta_under_paragraphs' ? cta?.size.height ?? 0 : 0;

  return paragraphsHeights + paragraphsPaddings + ctaHeight;
}

function getTitleColumnHeight(titleHeight: number, cta?: CtaView) {
  const ctaHeight =
    cta?.position === 'moreCta_under_title' ? cta.size.height ?? 0 : 0;

  const margin = ctaHeight > 0 ? cta?.marginTop ?? 0 : 0;

  return titleHeight + ctaHeight + margin;
}

const getColumnsHeight = (
  titleHeight: number,
  paragraphs: ParagraphView[],
  cta?: CtaView
): number => {
  const totalParagraphsHeight = getParagraphsHeight(paragraphs, cta);

  const totalTitleHeight = getTitleColumnHeight(titleHeight, cta);

  return Math.max(totalParagraphsHeight, totalTitleHeight);
};

/** Page Calculations */

const getPageHeight = (columnsHeight: number): number =>
  columnsHeight +
  scaleOnFigmaPageWidth(Constants.FIGMA_PAGE_TOP_PADDING) +
  scaleOnFigmaPageWidth(Constants.FIGMA_PAGE_TOP_PADDING);

const getPage = (width: number, height: number): { size: PlaneSize } => ({
  size: { width, height },
});

const getColumnWidth = (...paddings: number[]) =>
  paddings.reduce(
    (acc, padding) => acc - padding,
    Dimensions.get('window').width / 2
  );

/** Title Calculations */

const getTitleColumnWidth = (): number => {
  const innerPadding = scaleOnFigmaPageWidth(Constants.FIGMA_COLUMNS_GAP) / 2;
  const outerPadding = scaleOnFigmaPageWidth(
    Constants.FIGMA_PAGE_HORIZONTAL_PADDING
  );

  return getColumnWidth(innerPadding, outerPadding);
};

const getTitleColumnSize = (width: number, height: number): PlaneSize => ({
  width,
  height,
});

const getTitleColumnPositionX = (titleColumnWidth: number): number =>
  -scaleOnFigmaPageWidth(Constants.FIGMA_COLUMNS_GAP) / 2 -
  titleColumnWidth / 2;

const getTitle = async ({
  section,
  columnWidth,
}: {
  section: HeroAndTextView;
  columnWidth: number;
}): Promise<{ size: PlaneSize; props: CardTextProps }> => {
  const props: CardTextProps = {
    text: section.dtoData.title,
    fontAsset: Assets.Font_Gilroy_Bold,
    fontSize: 48,
    lineHeight: 50 / 48,
    letterSpacing: 0.03,
    maxWidth: columnWidth,
    color: section.dtoData.fontColour ?? 'black',
  };

  const size = await getTextBlockSize(props);
  return { size, props };
};

/** Paragraphs Calculations */

const getParagraphColumnWidth = getTitleColumnWidth;

const getParagraphsColumnPositionX = (paragraphsColumnWidth: number): number =>
  scaleOnFigmaPageWidth(Constants.FIGMA_COLUMNS_GAP) / 2 +
  paragraphsColumnWidth / 2;

const getColumnsPositionY = (
  columnsHeight: number,
  pageHeight: number
): number =>
  pageHeight / 2 -
  scaleOnFigmaPageWidth(Constants.FIGMA_PAGE_TOP_PADDING) -
  columnsHeight / 2;

export const getParagraphsText = (section: HeroAndTextView): string[] =>
  getParagraphs(section.dtoData.text);

const getParagraphsViews = async (
  texts: string[],
  maxWidth: number,
  fontColour?: string
): Promise<ParagraphView[]> =>
  Promise.all(
    texts.map((text, index) =>
      getParagraphView({ text, index, maxWidth, fontColour })
    )
  );

type GetParagraphViewParams = {
  text: string;
  index: number;
  maxWidth: number;
  fontColour?: string;
};

const getParagraphView = async ({
  text,
  index,
  maxWidth,
  fontColour,
}: GetParagraphViewParams): Promise<ParagraphView> => {
  const fontAsset =
    index === 0 ? Assets.Font_Gilroy_Bold : Assets.Font_Gilroy_Medium;

  const props: CardTextProps = {
    text,
    fontAsset,
    fontSize: 18,
    maxWidth,
    color: fontColour ?? 'black',
    lineHeight: 22 / 18,
    letterSpacing: 0.03,
  };

  const size = await getTextBlockSize(props);
  // we assign maxWidth to paragraph size to make sure that the text will not wrap-around
  // before reaching full length of the column
  return { props, size: { width: maxWidth, height: size.height } };
};

const hasCta = (section: HeroAndTextView): boolean =>
  Boolean(section.dtoData.linkButtonUrl) &&
  Boolean(section.dtoData.linkButtonTitle);

const getCta = (
  section: HeroAndTextView,
  position: CtaView['position']
): CtaView | undefined =>
  hasCta(section)
    ? {
        position,
        size: { width: 168, height: 47 },
        marginTop: 40,
        props: {
          text: section.dtoData.linkButtonTitle,
          fontSize: 16,
          fontAsset: Assets.Font_Gilroy_SemiBold,
          lineHeight: 19 / 16,
          letterSpacing: 0.03,
        },
      }
    : undefined;
